<script setup lang="ts">
import { useCountUp } from '@/composables/useCountUp'
import type { UserCredits } from '@/domain/entities/UserProfile'

interface Props {
	credits: UserCredits | null
	isSalesUser: boolean
	loading: boolean
}

const props = withDefaults(defineProps<Props>(), {
	credits: null,
	isSalesUser: false,
	loading: false,
})

function isInfinite(value: number): boolean {
	return value === -1 || value > 100_000
}

function formatValue(value: number): string {
	if (value < 0 && value !== -1) return '0'
	// Show the exact credit balance (Vue 2 parity) — no k/M abbreviation.
	return String(value)
}

// Values tween on change (purchases, exports) with a brief glow; the glow
// re-triggers via the :key on the raw source value
const { value: exportCredit } = useCountUp(() => props.credits?.availableExportCredit ?? 0)
const { value: mobileCredit } = useCountUp(() => props.credits?.availableMobileCredit ?? 0)
const { value: searchCredit } = useCountUp(() => props.credits?.availableSearchCredit ?? 0)
</script>

<template>
	<div class="credits-display d-flex align-center ga-1">
		<template v-if="loading">
			<v-skeleton-loader
				v-for="n in 3"
				:key="n"
				type="chip"
				width="64"
			/>
		</template>
		<template v-else-if="credits">
			<v-tooltip
				v-if="!isSalesUser"
				location="bottom"
			>
				<template #activator="{ props: tip }">
					<div
						v-bind="tip"
						class="credits-display__item"
						:class="{ 'credits-display__item--warning': credits.availableExportCredit === 0 }"
					>
						<span class="credits-display__icon credits-display__icon--export">
							<i class="fa-solid fa-coins" />
						</span>
						<span
							:key="credits.availableExportCredit"
							class="credits-display__value font-numeric"
							>{{ formatValue(exportCredit) }}</span
						>
					</div>
				</template>
				Export Credits Remaining
			</v-tooltip>

			<v-tooltip
				v-if="!isSalesUser"
				location="bottom"
			>
				<template #activator="{ props: tip }">
					<div
						v-bind="tip"
						class="credits-display__item"
						:class="{ 'credits-display__item--warning': credits.availableMobileCredit === 0 }"
					>
						<span class="credits-display__icon credits-display__icon--mobile">
							<i class="fa-solid fa-mobile-screen" />
						</span>
						<span
							:key="credits.availableMobileCredit"
							class="credits-display__value font-numeric"
							>{{ formatValue(mobileCredit) }}</span
						>
					</div>
				</template>
				Mobile Credits Remaining
			</v-tooltip>

			<v-tooltip
				v-if="!isInfinite(credits.availableSearchCredit)"
				location="bottom"
			>
				<template #activator="{ props: tip }">
					<div
						v-bind="tip"
						class="credits-display__item"
						:class="{ 'credits-display__item--warning': credits.availableSearchCredit === 0 }"
					>
						<span class="credits-display__icon credits-display__icon--search">
							<i class="fa-solid fa-magnifying-glass" />
						</span>
						<span
							:key="credits.availableSearchCredit"
							class="credits-display__value font-numeric"
							>{{ formatValue(searchCredit) }}</span
						>
					</div>
				</template>
				Search Credits Remaining
			</v-tooltip>
		</template>
	</div>
</template>

<style scoped lang="scss">
.credits-display {
	flex-shrink: 0;

	&__item {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 4px 10px 4px 4px;
		border-radius: 20px;
		cursor: default;
		transition: background-color 200ms ease;

		&:hover {
			background-color: rgba(var(--v-border-color), 0.08);
		}

		&--warning .credits-display__value {
			color: rgb(var(--v-theme-warning));
			font-weight: 700;
		}
	}

	&__icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 26px;
		height: 26px;
		border-radius: 8px;
		font-size: 11px;
		color: #fff;
		flex-shrink: 0;

		&--export {
			background-color: rgb(var(--v-theme-primary));
		}

		&--mobile {
			background-color: rgb(var(--v-theme-secondary));
		}

		&--search {
			background-color: rgb(var(--v-theme-success));
		}
	}

	&__value {
		font-size: 0.8125rem;
		font-weight: 600;
		color: rgb(var(--v-theme-on-surface));
		line-height: 1;
		// Brief highlight whenever the underlying credit value changes (re-keyed)
		animation: count-glow 600ms ease;
	}
}
</style>
